import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Formik, Form, Field } from 'formik';
import makeStyles from '@mui/styles/makeStyles';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';

import { DialogTitleWithClose } from 'ui';
import { useDispatch, useSelector } from 'react-redux';
import { requestConfirmationChangedAction } from 'store/entity/entityActions';
import { getCofaceRequestsAllowed } from 'selectors/creditReviewSelectors';

const useStyles = makeStyles(() => ({
  dialogContent: {
    minWidth: '400px',
  },
}));

const RequestConfirmationModal = ({ onClose, open, requestsCount }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { t } = useTranslation();
  const initialValues = {
    dontShowAgain: false,
  };

  const cofaceRequestsAllowed = useSelector(getCofaceRequestsAllowed);

  const handleSubmit = values => {
    dispatch(
      requestConfirmationChangedAction({ confirmed: true, autoConfirm: values.dontShowAgain }),
    );
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      {requestsCount <= cofaceRequestsAllowed ? (
        <>
          <DialogTitleWithClose onClose={onClose}>
            {t(
              `Credit Review: Proceed with the request (${requestsCount} of ${cofaceRequestsAllowed} available)`,
            )}
          </DialogTitleWithClose>
          <Formik initialValues={initialValues} onSubmit={handleSubmit}>
            {({ isSubmitting }) => {
              return (
                <Form noValidate>
                  <DialogContent className={classes.dialogContent}>
                    <DialogContentText>
                      {t(
                        `By proceeding with this request the number of Credit Reviews included with 
                      your license will be deducted by ${requestsCount}. Executed requests are not refundable.`,
                      )}
                      <br />
                      <br />
                      {t('Are you sure you wish to continue?')}
                    </DialogContentText>
                    <FormGroup className={classes.types} row>
                      <Field name="dontShowAgain">
                        {({ field }) => (
                          <FormControlLabel
                            control={<Checkbox {...field} color="primary" />}
                            label={t(
                              'Don’t show this message again and automatically confirm requests going forward',
                            )}
                          />
                        )}
                      </Field>
                    </FormGroup>
                  </DialogContent>
                  <DialogActions>
                    <Button variant="outlined" onClick={onClose}>
                      {t('Cancel')}
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                    >
                      {t('Confirm')}
                    </Button>
                  </DialogActions>
                </Form>
              );
            }}
          </Formik>
        </>
      ) : (
        <>
          <DialogTitleWithClose onClose={onClose}>
            {t(
              `Credit Review: You have requested ${requestsCount} of ${cofaceRequestsAllowed} requests available`,
            )}
          </DialogTitleWithClose>
          <DialogContent className={classes.dialogContent}>
            <DialogContentText>
              {t(
                `Number of requests exeeds the number of available requests by ${requestsCount -
                  cofaceRequestsAllowed}.`,
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" color="primary" onClick={onClose}>
              {t('Close')}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

RequestConfirmationModal.defaultProps = {
  open: false,
  onClose: () => null,
};

RequestConfirmationModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  requestsCount: PropTypes.number.isRequired,
};

export default RequestConfirmationModal;
